import React, { useState, useCallback, useEffect, useRef } from "react";
import { fetchDataGranularityLevelsAPI } from "../../Dashboard/filtersAPI";
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  withStyles,
  Checkbox,
  ListItemText,
  makeStyles,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import Check from "@material-ui/icons/Check";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import EventTwoToneIcon from "@material-ui/icons/EventTwoTone";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { fetchStageMappingAPI, updateCampaignDetailsAPI } from "../stepperAPI";
import { useStyles } from './AddCampaignIDStyles';
import jwt_decode from "jwt-decode";

// Add global styles for ios-switch and checked-switch classes
const globalStyles = `
  .ios-switch > * {
    height: 22px !important;
  }
  .ios-switch .MuiSwitch-switchBase .MuiSwitch-track {
    background-color: #c6c6c6 !important;
  }
  .ios-switch .MuiSwitch-switchBase {
    left: 2px;
    top: -1px;
  }
  .ios-switch .MuiSwitch-switchBase.Mui-checked {
    left: auto;
    right: 18px;
  }
  .ios-switch .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track {
    background-color: #1890ff !important;
  }
  .checked-switch {
    position: absolute;
    width: 16px !important;
    left: 12px;
    top: -3px;
    color: white;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = globalStyles;
  if (!document.head.querySelector('style[data-add-campaign-styles]')) {
    styleSheet.setAttribute('data-add-campaign-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}

// Custom styled switch with checkmark support
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const UOM_OPTIONS = ["KG", "G", "MG", "L", "ML", "Units"];
// Data Granularity: dynamic from API, with defaults as fallback
const DEFAULT_DATA_GRANULARITY_OPTIONS = ["Operation Level", "Unit Procedure Level"];
const DEFAULT_VIEW_OPTIONS = ["System", "Overall"];

const DEFAULT_TABS = [
  "Materials & Consumption",
  "CPPs",
  "In-Process Tests",
  "Information Only Tests",
  "Product Release Tests",
  "CQAs",
  "Deviations",
  "Change Controls",
  "Cycle Time",
  "Equipment",
  "Yield",
  "Documents",
];

// Column configurations for each tab
const COLUMN_CONFIGS = {
  "Materials & Consumption": [
    "Procedure Number",
    "Material Description",
    "Sponsor Material ID",
    "CMO Material ID",
    "CMO Batch Number",
    "Target Quantity",
    "Actual Quantity",
    "UOM",
    "Quantity Variance (%)",
  ],
  "CPPs": [
    "Procedure Number",
    "Parameter",
    "Set Point",
    "Range",
    "Actual Value",
    "Time Series Data",
    "UOM",
  ],
  "In-Process Tests": [
    "Procedure Number",
    "Method Number",
    "Method Name",
    "Sample ID",
    "Sample Description",
    "Specification Limit",
    "UOM",
    "Analytical Report Number",
    "Result Value",
    "Result (Approved / Rejected)",
    "COAs (Yes / No)",
  ],
  "Product Release Tests": [
    "Method Number",
    "Method Name",
    "Sample ID",
    "Sample Description",
    "Specification Limit",
    "UOM",
    "Analytical Report Number",
    "Result Value",
    "Result (Approved / Rejected)",
    "CQAs (Yes / No)",
  ],
  "Deviations": [
    "Deviation Number",
    "Procedure Number",
    "Deviation Title",
    "Reporting Date",
    "Initial Deviation Notification",
    "Deviation Assessment Report",
    "Deviation Closure Report",
    "CMO Deviation Status",
    "Sponsor Deviation Status",
  ],
  "Change Controls": [
    "Procedure Number",
    "Change Control Title",
    "Change Control Type",
    "Change Control Number",
    "Initial Procedure Number",
    "Final Procedure Number",
    "Initial Change Control Description",
    "Change Control Report",
    "CMO Change Control Status",
    "Affected Batches",
  ],
  "Cycle Time": [
    "Procedure Number",
    "Target Start Time",
    "Actual Start Time",
    "Target End Time",
    "Actual End Time",
    "Planned Cycle Time",
    "Actual Cycle Time",
  ],
  "Equipment": [
    "Equipment ID",
    "Equipment Type",
    "Equipment Specification-1",
    "Equipment Specification-2",
    "Equipment Specification-3",
    "Location",
  ],
  "Yield": [
    "Procedure Number",
    "Planned Product Quantity",
    "Actual Product Quantity",
    "UOM",
    "Yield (%)",
    "Storage Condition",
    "Storage Location",
  ],
  "Documents": [
    "Document Type",
    "File Name",
    "Upload Date",
  ],
};

// Default toggle states for columns
const DEFAULT_STATES = {
  "Materials & Consumption": {
    overallView: {
      "Procedure Number": true,
      "Material Description": true,
      "Sponsor Material ID": true,
      "CMO Material ID": true,
      "CMO Batch Number": true,
      "Target Quantity": true,
      "Actual Quantity": true,
      "UOM": true,
      "Quantity Variance (%)": true,
    },
    systemView: {
      "Procedure Number": false,
      "Material Description": true,
      "Sponsor Material ID": true,
      "CMO Material ID": true,
      "CMO Batch Number": true,
      "Target Quantity": false,
      "Actual Quantity": true,
      "UOM": true,
      "Quantity Variance (%)": false,
    },
  },
  "CPPs": {
    overallView: {
      "Procedure Number": true,
      "Parameter": true,
      "Set Point": false,
      "Range": true,
      "Actual Value": true,
      "Time Series Data": false,
      "UOM": true,
    },
    systemView: {
      "Procedure Number": false,
      "Parameter": false,
      "Set Point": false,
      "Range": false,
      "Actual Value": false,
      "Time Series Data": false,
      "UOM": false,
    },
  },
  "In-Process Tests": {
    overallView: {
      "Procedure Number": true,
      "Method Number": true,
      "Method Name": false,
      "Sample ID": true,
      "Sample Description": true,
      "Specification Limit": true,
      "UOM": true,
      "Analytical Report Number": true,
      "Result Value": true,
      "Result (Approved / Rejected)": true,
      "COAs (Yes / No)": true,
    },
    systemView: {
      "Procedure Number": true,
      "Method Number": true,
      "Method Name": false,
      "Sample ID": true,
      "Sample Description": true,
      "Specification Limit": true,
      "UOM": true,
      "Analytical Report Number": true,
      "Result Value": true,
      "Result (Approved / Rejected)": true,
      "COAs (Yes / No)": true,
    },
  },
  "Product Release Tests": {
    overallView: {
      "Method Number": true,
      "Method Name": false,
      "Sample ID": true,
      "Sample Description": true,
      "Specification Limit": true,
      "UOM": true,
      "Analytical Report Number": true,
      "Result Value": true,
      "Result (Approved / Rejected)": true,
      "CQAs (Yes / No)": true,
    },
    systemView: {
      "Method Number": true,
      "Method Name": false,
      "Sample ID": true,
      "Sample Description": true,
      "Specification Limit": true,
      "UOM": true,
      "Analytical Report Number": true,
      "Result Value": true,
      "Result (Approved / Rejected)": true,
      "CQAs (Yes / No)": true,
    },
  },
  "Deviations": {
    overallView: {
      "Deviation Number": true,
      "Procedure Number": false,
      "Deviation Title": false,
      "Reporting Date": true,
      "Initial Deviation Notification": true,
      "Deviation Assessment Report": true,
      "Deviation Closure Report": true,
      "CMO Deviation Status": true,
      "Sponsor Deviation Status": true,
    },
    systemView: {
      "Deviation Number": true,
      "Procedure Number": false,
      "Deviation Title": false,
      "Reporting Date": true,
      "Initial Deviation Notification": true,
      "Deviation Assessment Report": true,
      "Deviation Closure Report": true,
      "CMO Deviation Status": true,
      "Sponsor Deviation Status": true,
    },
  },
  "Change Controls": {
    overallView: {
      "Procedure Number": false,
      "Change Control Title": false,
      "Change Control Type": true,
      "Change Control Number": true,
      "Initial Procedure Number": true,
      "Final Procedure Number": true,
      "Initial Change Control Description": true,
      "Change Control Report": true,
      "CMO Change Control Status": true,
      "Affected Batches": false,
    },
    systemView: {
      "Procedure Number": false,
      "Change Control Title": false,
      "Change Control Type": true,
      "Change Control Number": true,
      "Initial Procedure Number": true,
      "Final Procedure Number": true,
      "Initial Change Control Description": true,
      "Change Control Report": true,
      "CMO Change Control Status": true,
      "Affected Batches": false,
    },
  },
  "Cycle Time": {
    overallView: {
      "Procedure Number": true,
      "Target Start Time": false,
      "Actual Start Time": true,
      "Target End Time": false,
      "Actual End Time": true,
      "Planned Cycle Time": false,
      "Actual Cycle Time": true,
    },
    systemView: {
      "Procedure Number": false,
      "Target Start Time": false,
      "Actual Start Time": false,
      "Target End Time": false,
      "Actual End Time": false,
      "Planned Cycle Time": false,
      "Actual Cycle Time": false,
    },
  },
  "Equipment": {
    overallView: {
      "Equipment ID": true,
      "Equipment Type": true,
      "Equipment Specification-1": true,
      "Equipment Specification-2": true,
      "Equipment Specification-3": true,
      "Location": false,
    },
    systemView: {
      "Equipment ID": true,
      "Equipment Type": true,
      "Equipment Specification-1": true,
      "Equipment Specification-2": true,
      "Equipment Specification-3": true,
      "Location": false,
    },
  },
  "Yield": {
    overallView: {
      "Procedure Number": false,
      "Planned Product Quantity": false,
      "Actual Product Quantity": false,
      "UOM": false,
      "Yield (%)": false,
      "Storage Condition": false,
      "Storage Location": false,
    },
    systemView: {
      "Procedure Number": false,
      "Planned Product Quantity": false,
      "Actual Product Quantity": false,
      "UOM": false,
      "Yield (%)": false,
      "Storage Condition": false,
      "Storage Location": false,
    },
  },
  "Documents": {
    overallView: {
      "Document Type": true,
      "File Name": true,
      "Upload Date": true,
    },
    systemView: {
      "Document Type": true,
      "File Name": true,
      "Upload Date": true,
    },
  },
};

const AddCampaignID = ({
  formData,
  updateStepData,
  classes: parentClasses,
  handleNext,
  handleBack,
  activeStep,
  stepsLength,
  markApiCallSuccessful,
  hideStepActions = false, // Hide navigation buttons when used in review page
  readOnly = false, // Read-only mode for review page
  isAddingNewCampaign = false, // When true, shows empty form for new campaign
  previousCampaignData = null, // Previous campaign data when adding new
  onCancelAddNew = null, // Callback to cancel add new campaign
}) => {
  const classes = useStyles();
  const [dataGranularityOptions, setDataGranularityOptions] = useState(
    DEFAULT_DATA_GRANULARITY_OPTIONS
  );

  // Project-level dates from first step (Product Details)
  // Campaign dates must always be within this range
  const projectStartDate =
    formData?.productDetails?.projectStartDate || null;
  const projectEndDate =
    formData?.productDetails?.projectEndDate || null;
  const [stepData, setStepData] = useState(() => {
    const saved = formData?.campaignID || {};
    
    // Initialize column configurations
    const savedColumnConfigs = saved.columnConfigurations || formData?.columnConfigurations || {};
    const columnConfigs = {};
    
    Object.keys(COLUMN_CONFIGS).forEach((tabName) => {
      columnConfigs[tabName] = {
        columns: COLUMN_CONFIGS[tabName].map((colName) => ({
          name: colName,
          overallView: savedColumnConfigs[tabName]?.columns?.find((c) => c.name === colName)?.overallView ?? 
                      DEFAULT_STATES[tabName]?.overallView?.[colName] ?? true,
          systemView: savedColumnConfigs[tabName]?.columns?.find((c) => c.name === colName)?.systemView ?? 
                      DEFAULT_STATES[tabName]?.systemView?.[colName] ?? true,
          renamedTab: savedColumnConfigs[tabName]?.columns?.find((c) => c.name === colName)?.renamedTab ?? "NA",
        })),
        newColumnName: savedColumnConfigs[tabName]?.newColumnName || "",
      };
    });
    
    return {
      collabrixCampaignID: saved.collabrixCampaignID || null,
      campaignID: saved.campaignID || "",
      poNumber: saved.poNumber || "",
      productQuantity: saved.productQuantity || "",
      numberOfStages: saved.numberOfStages || "",
      uom: saved.uom || "KG",
      defaultView: saved.defaultView || "System",
      dataGranularity: saved.dataGranularity || "Operation Level",
      campaignStartDate: saved.campaignStartDate || null,
      campaignEndDate: saved.campaignEndDate || null,
      manufacturingFacility: saved.manufacturingFacility || "",
      // Stage Mapping - initially empty
      stageMapping: saved.stageMapping || [],
      // Materials not provided by BMS - initially empty
      materialsNotProvidedByBMS: (saved.materialsNotProvidedByBMS || []).map(material => ({
        ...material,
        usedInStage: Array.isArray(material.usedInStage) ? material.usedInStage : (material.usedInStage ? [material.usedInStage] : []),
      })),
      // Materials provided by BMS - initially empty
      materialsProvidedByBMS: (saved.materialsProvidedByBMS || []).map(material => ({
        ...material,
        usedInStage: Array.isArray(material.usedInStage) ? material.usedInStage : (material.usedInStage ? [material.usedInStage] : []),
      })),
      tabConfiguration: saved.tabConfiguration ? {
        overallView: (saved.tabConfiguration.overallView || DEFAULT_TABS.map((tab, index) => ({
          id: index + 1,
          name: tab,
          selected: !["CPPs", "Information Only Tests", "Cycle Time"].includes(tab),
          renamedTab: "NA",
        }))).map(tab => ({
          ...tab,
          renamedTab: tab.renamedTab || "NA", // Ensure renamedTab defaults to "NA"
        })),
        systemView: (saved.tabConfiguration.systemView || DEFAULT_TABS.map((tab, index) => ({
          id: index + 1,
          name: tab,
          selected: !["Information Only Tests", "Cycle Time"].includes(tab),
          renamedTab: "NA",
        }))).map(tab => ({
          ...tab,
          renamedTab: tab.renamedTab || "NA", // Ensure renamedTab defaults to "NA"
        })),
        customTabs: saved.tabConfiguration.customTabs || [],
      } : {
        overallView: DEFAULT_TABS.map((tab, index) => ({
          id: index + 1,
          name: tab,
          selected: !["CPPs", "Information Only Tests", "Cycle Time"].includes(tab),
          renamedTab: "NA",
        })),
        systemView: DEFAULT_TABS.map((tab, index) => ({
          id: index + 1,
          name: tab,
          selected: !["Information Only Tests", "Cycle Time"].includes(tab),
          renamedTab: "NA",
        })),
        customTabs: [],
      },
      columnConfigurations: columnConfigs,
    };
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoadingStageMapping, setIsLoadingStageMapping] = useState(false);
  const [stageMappingError, setStageMappingError] = useState(null);

  // Load data granularity options from API on mount
  useEffect(() => {
    const loadDataGranularityLevels = async () => {
      try {
        const response = await fetchDataGranularityLevelsAPI();
        if (response?.status && Array.isArray(response.data) && response.data.length > 0) {
          setDataGranularityOptions(response.data);
        } else {
          // Fallback: keep defaults as Operation / Unit Procedure, mapped to simple objects
          setDataGranularityOptions(
            DEFAULT_DATA_GRANULARITY_OPTIONS.map((name, index) => ({
              dataGranularityLevelID: index + 1,
              dataGranularityLevelName: name,
              activated: true,
            }))
          );
        }
      } catch (e) {
        setDataGranularityOptions(
          DEFAULT_DATA_GRANULARITY_OPTIONS.map((name, index) => ({
            dataGranularityLevelID: index + 1,
            dataGranularityLevelName: name,
            activated: true,
          }))
        );
      }
    };

    loadDataGranularityLevels();
  }, []);

  // Sync stepData with formData when formData changes (especially when adding new campaign / edit mode)
  useEffect(() => {
    const saved = formData?.campaignID || {};

    // ===== Derive stage mapping from API if not already saved =====
    const apiStageDetails = saved.campaignStageDetails || [];
    const derivedStageMapping =
      saved.stageMapping && saved.stageMapping.length > 0
        ? saved.stageMapping
        : apiStageDetails.map((stage) => ({
            stageNumber: stage.stageNumber,
            sponsorStageID: stage.sponsorStageID || "",
            cmoStageID: stage.cmoStageID || "",
            selected: stage.isActive ?? true,
            collabrixCampaignStageID: stage.collabrixCampaignStageID || null,
          }));

    // ===== Derive materials from campaignBOMDetails if not already saved =====
    const apiBomDetails = saved.campaignBOMDetails || [];

    const derivedMaterialsNotProvidedByBMS =
      (saved.materialsNotProvidedByBMS && saved.materialsNotProvidedByBMS.length > 0)
        ? saved.materialsNotProvidedByBMS
        : apiBomDetails
            .filter((bom) => bom.materialProvidedBySponsor === false)
            .map((bom) => ({
              collabrixBomID: bom.collabrixBomID || null,
              cmoMaterialCode: bom.cmoMaterialCode || "",
              cmoMaterialDescription: bom.cmoMaterialDescription || "",
              usedInStage: bom.cmoStageIDs ? bom.cmoStageIDs.split(",").filter(Boolean) : [],
              cmoUOM: bom.cmoUOM || "",
              supplier: bom.supplierName || "",
              countryOfOrigin: bom.countryOfOrigin || "",
              bo_material_code: bom.sponsorMaterialCode || "",
              material_description: bom.sponsorMaterialDescription || "",
              bo_material_UOM: bom.sponsorUOM || "",
            }));

    const derivedMaterialsProvidedByBMS =
      (saved.materialsProvidedByBMS && saved.materialsProvidedByBMS.length > 0)
        ? saved.materialsProvidedByBMS
        : apiBomDetails
            .filter((bom) => bom.materialProvidedBySponsor === true)
            .map((bom) => ({
              collabrixBomID: bom.collabrixBomID || null,
              cmoMaterialCode: bom.cmoMaterialCode || "",
              cmoMaterialDescription: bom.cmoMaterialDescription || "",
              usedInStage: bom.cmoStageIDs ? bom.cmoStageIDs.split(",").filter(Boolean) : [],
              cmoUOM: bom.cmoUOM || "",
              supplierName: bom.supplierName || "",
              countryOfOrigin: bom.countryOfOrigin || "",
              bmsMaterialCode: bom.sponsorMaterialCode || "",
              bmsMaterialDescription: bom.sponsorMaterialDescription || "",
              bmsUOM: bom.sponsorUOM || "",
            }));

    // Flag to detect edit-from-API vs new project
    const isEditFromApi =
      !!saved.collabrixCampaignID ||
      (apiStageDetails && apiStageDetails.length > 0) ||
      (apiBomDetails && apiBomDetails.length > 0);

    // ===== Derive tab configuration from campaignTabConfiguration if not already saved =====
    const apiTabConfig = saved.campaignTabConfiguration || [];
    let derivedTabConfiguration = saved.tabConfiguration;

    if (!derivedTabConfiguration) {
      if (isEditFromApi && apiTabConfig.length > 0) {
        // Strictly map from API for edit mode – only tabs returned by backend
        const overallViewTabs = [];
        const systemViewTabs = [];
        const customTabs = [];

        apiTabConfig.forEach((tab, index) => {
          const view = (tab.viewTab || "").toLowerCase();
          const base = {
            id: index + 1,
            name: tab.tabName,
            selected: !!tab.isActive,
            renamedTab:
              tab.newTabName && tab.newTabName !== "NA"
                ? tab.newTabName
                : "NA",
            markForDelete: tab.markForDelete || false,
            collabrixTabConfigID: tab.collabrixTabConfigID || null,
          };

          // Treat tabs not in DEFAULT_TABS as custom
          const isDefault = DEFAULT_TABS.includes(tab.tabName);
          const targetArray =
            view === "system" || view === "systemview"
              ? systemViewTabs
              : overallViewTabs;

          if (isDefault) {
            targetArray.push(base);
          } else {
            customTabs.push({
              ...base,
              viewTab:
                view === "system" || view === "systemview"
                  ? "systemView"
                  : "overallView",
            });
          }
        });

        derivedTabConfiguration = {
          overallView: overallViewTabs,
          systemView: systemViewTabs,
          customTabs,
        };
      } else {
        // New project – use default tabs
        derivedTabConfiguration = {
          overallView: DEFAULT_TABS.map((tab, index) => ({
            id: index + 1,
            name: tab,
            selected: !["CPPs", "Information Only Tests", "Cycle Time"].includes(
              tab
            ),
            renamedTab: "NA",
          })),
          systemView: DEFAULT_TABS.map((tab, index) => ({
            id: index + 1,
            name: tab,
            selected: !["Information Only Tests", "Cycle Time"].includes(tab),
            renamedTab: "NA",
          })),
          customTabs: [],
        };
      }
    }

    // ===== Derive column configurations from campaignColumnConfiguration if not already saved =====
    const apiColumnConfig = saved.campaignColumnConfiguration || [];

    // Initialize column configurations
    let columnConfigs = {};

    if (saved.columnConfigurations) {
      // If user has already saved column configurations, respect that
      const savedColumnConfigs = saved.columnConfigurations || {};

      Object.keys(COLUMN_CONFIGS).forEach((tabName) => {
        columnConfigs[tabName] = {
          columns: COLUMN_CONFIGS[tabName].map((colName) => {
            const existingCol =
              savedColumnConfigs[tabName]?.columns?.find(
                (c) => c.name === colName
              ) || null;

            return {
              name: colName,
              overallView:
                existingCol?.overallView ??
                DEFAULT_STATES[tabName]?.overallView?.[colName] ??
                true,
              systemView:
                existingCol?.systemView ??
                DEFAULT_STATES[tabName]?.systemView?.[colName] ??
                true,
              renamedTab: existingCol?.renamedTab ?? "NA",
              collabrixTabConfigID: existingCol?.collabrixTabConfigID || null,
              collabrixColumnConfigID:
                existingCol?.collabrixColumnConfigID || null,
            };
          }),
          newColumnName: savedColumnConfigs[tabName]?.newColumnName || "",
        };
      });
    } else if (isEditFromApi && apiColumnConfig.length > 0) {
      // Edit mode – build ONLY from API column configurations
      columnConfigs = {};

      // Map collabrixTabConfigID -> tabName using apiTabConfig
      const tabIdToName = {};
      apiTabConfig.forEach((tab) => {
        if (tab.collabrixTabConfigID && tab.tabName) {
          tabIdToName[tab.collabrixTabConfigID] = tab.tabName;
        }
      });

      apiColumnConfig.forEach((col) => {
        const tabName =
          tabIdToName[col.collabrixTabConfigID] || "Materials & Consumption";

        if (!columnConfigs[tabName]) {
          columnConfigs[tabName] = {
            columns: [],
            newColumnName: "",
          };
        }

        columnConfigs[tabName].columns.push({
          name: col.columnName || "",
          overallView: !!col.isOverallViewSelected,
          systemView: !!col.isSystemViewSelected,
          renamedTab:
            col.newColumnName && col.newColumnName !== "NA"
              ? col.newColumnName
              : "NA",
          collabrixTabConfigID: col.collabrixTabConfigID || null,
          collabrixColumnConfigID: col.collabrixColumnConfigID || null,
        });
      });
    } else {
      // New project – use full default column set with DEFAULT_STATES
      Object.keys(COLUMN_CONFIGS).forEach((tabName) => {
        columnConfigs[tabName] = {
          columns: COLUMN_CONFIGS[tabName].map((colName) => ({
            name: colName,
            overallView:
              DEFAULT_STATES[tabName]?.overallView?.[colName] ?? true,
            systemView:
              DEFAULT_STATES[tabName]?.systemView?.[colName] ?? true,
            renamedTab: "NA",
          })),
          newColumnName: "",
        };
      });
    }

    setStepData({
      collabrixCampaignID: saved.collabrixCampaignID || null,
      campaignID: saved.campaignID || "",
      poNumber: saved.poNumber || "",
      productQuantity: saved.productQuantity || "",
      numberOfStages: saved.numberOfStages || "",
      uom: saved.uom || "KG",
      defaultView: saved.defaultView || "System",
      dataGranularity: saved.dataGranularity || "Operation Level",
      campaignStartDate: saved.campaignStartDate || null,
      campaignEndDate: saved.campaignEndDate || null,
      manufacturingFacility: saved.manufacturingFacility || "",
      stageMapping: derivedStageMapping,
      materialsNotProvidedByBMS: derivedMaterialsNotProvidedByBMS.map(
        (material) => ({
          ...material,
          usedInStage: Array.isArray(material.usedInStage)
            ? material.usedInStage
            : material.usedInStage
            ? [material.usedInStage]
            : [],
        })
      ),
      materialsProvidedByBMS: derivedMaterialsProvidedByBMS.map(
        (material) => ({
          ...material,
          usedInStage: Array.isArray(material.usedInStage)
            ? material.usedInStage
            : material.usedInStage
            ? [material.usedInStage]
            : [],
        })
      ),
      tabConfiguration: derivedTabConfiguration
        ? {
            overallView: derivedTabConfiguration.overallView.map((tab) => ({
              ...tab,
              renamedTab: tab.renamedTab || "NA",
            })),
            systemView: derivedTabConfiguration.systemView.map((tab) => ({
              ...tab,
              renamedTab: tab.renamedTab || "NA",
            })),
            customTabs: derivedTabConfiguration.customTabs || [],
          }
        : {
            overallView: DEFAULT_TABS.map((tab, index) => ({
              id: index + 1,
              name: tab,
              selected: !["CPPs", "Information Only Tests", "Cycle Time"].includes(
                tab
              ),
              renamedTab: "NA",
            })),
            systemView: DEFAULT_TABS.map((tab, index) => ({
              id: index + 1,
              name: tab,
              selected: !["Information Only Tests", "Cycle Time"].includes(tab),
              renamedTab: "NA",
            })),
            customTabs: [],
          },
      columnConfigurations: columnConfigs,
    });
  }, [formData?.campaignID, formData?.columnConfigurations]);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabValue, setEditingTabValue] = useState("");
  const [editingTabType, setEditingTabType] = useState(null); // 'overallView' or 'systemView'
  const [newTabNameOverall, setNewTabNameOverall] = useState(""); // Separate state for overall view
  const [newTabNameSystem, setNewTabNameSystem] = useState(""); // Separate state for system view

  // Column configuration state
  const [editingColumn, setEditingColumn] = useState(null); // { tabName, columnName }
  const [editingColumnValue, setEditingColumnValue] = useState("");

  // Helper function to check if a tab is newly added (not in default tabs)
  const isNewlyAddedTab = useCallback((tabName) => {
    return !DEFAULT_TABS.includes(tabName);
  }, []);

  // Helper function to check if a column is newly added (not in default COLUMN_CONFIGS)
  const isNewlyAddedColumn = useCallback((tabName, columnName) => {
    return !COLUMN_CONFIGS[tabName]?.includes(columnName);
  }, []);

  // Column configuration handlers
  const handleColumnToggle = useCallback((tabName, columnName, viewType) => {
    setStepData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const column = updated.columnConfigurations[tabName].columns.find((c) => c.name === columnName);
      if (column) {
        column[viewType] = !column[viewType];
      }
      updateStepData("campaignID", updated);
      return updated;
    });
  }, [updateStepData]);

  const handleSaveColumnRename = useCallback((tabName, columnName) => {
    setStepData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const col = updated.columnConfigurations[tabName].columns.find((c) => c.name === columnName);
      if (col) {
        col.renamedTab = editingColumnValue.trim() || "NA";
      }
      updateStepData("campaignID", updated);
      return updated;
    });
    setEditingColumn(null);
    setEditingColumnValue("");
  }, [editingColumnValue, updateStepData]);

  const handleCancelColumnRename = useCallback(() => {
    setEditingColumn(null);
    setEditingColumnValue("");
  }, []);

  const handleAddColumn = useCallback((tabName) => {
    if (stepData.columnConfigurations[tabName].newColumnName.trim()) {
      setStepData((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        updated.columnConfigurations[tabName].columns.push({
          name: updated.columnConfigurations[tabName].newColumnName.trim(),
          overallView: true,
          systemView: true,
          renamedTab: "NA",
        });
        updated.columnConfigurations[tabName].newColumnName = "";
        updateStepData("campaignID", updated);
        return updated;
      });
    }
  }, [stepData, updateStepData]);

  const handleDeleteColumn = useCallback((tabName, columnName) => {
    setStepData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.columnConfigurations[tabName].columns = updated.columnConfigurations[tabName].columns.filter((c) => c.name !== columnName);
      updateStepData("campaignID", updated);
      return updated;
    });
  }, [updateStepData]);

  // Helper function to convert index to letter (0 -> A, 1 -> B, etc.)
  const getStageLetter = useCallback((index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
  }, []);

  const handleFieldChange = useCallback(
    (name, value) => {
      const updated = {
        ...stepData,
        [name]: value,
      };

      // When numberOfStages changes, automatically create/update stage rows
      if (name === "numberOfStages") {
        const numStages = Math.min(Math.max(parseInt(value, 10) || 0, 0), 10); // Limit to max 10
        const currentStages = updated.stageMapping || [];
        
        if (numStages === 0 || value === "") {
          // If number is 0 or empty, clear all stages
          updated.stageMapping = [];
          updated.numberOfStages = ""; // Clear the field
        } else {
          // Create exactly numStages empty rows
          const newStages = [];
          for (let i = 0; i < numStages; i++) {
            if (i < currentStages.length) {
              // Keep existing stage data if it exists
              newStages.push({
                ...currentStages[i],
                // If cmoStageID is empty, set default letter value
                cmoStageID: currentStages[i].cmoStageID || String.fromCharCode(65 + i),
              });
            } else {
              // Add new empty stage with default CMO Stage ID as letter
              newStages.push({
                id: Date.now() + i, // Unique ID
                stageNumber: i + 1, // Keep as number
                sponsorStageID: "",
                cmoStageID: String.fromCharCode(65 + i), // Default to letter (A, B, C, etc.)
                selected: true, // Default to selected
              });
            }
          }
          updated.stageMapping = newStages;
          updated.numberOfStages = numStages; // Update to the validated number
        }
      }

      setStepData(updated);
      updateStepData("campaignID", updated);

      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [stepData, errors, updateStepData]
  );

  // Stage Mapping handlers
  const handleAddStage = useCallback(() => {
    const newStage = {
      id: Date.now(),
      stageNumber: stepData.stageMapping.length + 1, // Keep as number
      sponsorStageID: "",
      cmoStageID: String.fromCharCode(65 + stepData.stageMapping.length), // Default to letter (A, B, C, etc.)
      selected: true,
    };
    const updated = {
      ...stepData,
      stageMapping: [...stepData.stageMapping, newStage],
    };
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleStageChange = useCallback((index, field, value) => {
    const updated = { ...stepData };
    updated.stageMapping[index][field] = value;
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleDeleteStage = useCallback((index) => {
    const updated = { ...stepData };
    updated.stageMapping.splice(index, 1);
    // Renumber stages and update CMO Stage IDs with letters
    updated.stageMapping.forEach((stage, idx) => {
      stage.stageNumber = idx + 1; // Keep as number
      // Update CMO Stage ID to letter if it's empty or needs to be reset
      if (!stage.cmoStageID || stage.cmoStageID.trim() === "") {
        stage.cmoStageID = String.fromCharCode(65 + idx); // Convert to letter (A, B, C, etc.)
      }
    });
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  // Materials not provided by BMS handlers
  const handleAddMaterialNotProvided = useCallback(() => {
    const newMaterial = {
      id: Date.now(),
      cmoMaterialCode: "",
      cmoMaterialDescription: "",
      usedInStage: [],
      cmoUOM: "KG",
      supplier: "",
      countryOfOrigin: "",
      selected: true,
    };
    const updated = {
      ...stepData,
      materialsNotProvidedByBMS: [...stepData.materialsNotProvidedByBMS, newMaterial],
    };
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleMaterialNotProvidedChange = useCallback((index, field, value) => {
    const updated = { ...stepData };
    updated.materialsNotProvidedByBMS[index][field] = value;
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleDeleteMaterialNotProvided = useCallback((index) => {
    const updated = { ...stepData };
    updated.materialsNotProvidedByBMS.splice(index, 1);
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  // Materials provided by BMS handlers
  const handleAddMaterialProvided = useCallback(() => {
    const newMaterial = {
      id: Date.now(),
      bmsMaterialCode: "",
      bmsMaterialDescription: "",
      cmoMaterialCode: "",
      cmoMaterialDescription: "",
      usedInStage: [],
      bmsUOM: "KG",
      cmoUOM: "KG",
      selected: true,
    };
    const updated = {
      ...stepData,
      materialsProvidedByBMS: [...stepData.materialsProvidedByBMS, newMaterial],
    };
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleMaterialProvidedChange = useCallback((index, field, value) => {
    const updated = { ...stepData };
    updated.materialsProvidedByBMS[index][field] = value;
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  const handleDeleteMaterialProvided = useCallback((index) => {
    const updated = { ...stepData };
    updated.materialsProvidedByBMS.splice(index, 1);
    setStepData(updated);
    updateStepData("campaignID", updated);
  }, [stepData, updateStepData]);

  // Get available stage options for "Used In Stage" dropdowns
  // Only return CMO Stage IDs that are selected (toggle ON) and have a CMO Stage ID entered
  const getAvailableStages = useCallback(() => {
    return stepData.stageMapping
      .filter((stage) => stage.selected && stage.cmoStageID && stage.cmoStageID.trim() !== "")
      .map((stage) => stage.cmoStageID)
      .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates
  }, [stepData.stageMapping]);


  // Helper to get collabrixProjectID from formData
  const getCollabrixProjectID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.collabrixProjectID || null;
      }
    } catch (error) {
      console.error("Error getting collabrixProjectID:", error);
    }
    return null;
  }, []);

  // Helper to get cdmoOrgID from formData
  const getCdmoOrgID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.cdmoOrgID || null;
      }
    } catch (error) {
      console.error("Error getting cdmoOrgID:", error);
    }
    return null;
  }, []);

  // Helper to get sponsorMosymphonyID from token
  const getSponsorMosymphonyID = useCallback(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        const decoded = jwt_decode(token);
        return decoded.organization_id || null;
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
    return null;
  }, []);

  // Helper to map dataGranularity to dataGranularityID
  const getDataGranularityID = useCallback((dataGranularity) => {
    // Map string values to IDs (this might need to be fetched from API)
    const mapping = {
      "Operation Level": 1,
      "Phase Level": 2,
      "Unit Procedure Level": 3,
    };
    return mapping[dataGranularity] || null;
  }, []);

  const handleSaveAndNext = useCallback(async () => {
    setIsSaving(true);
    setApiError(null); // Clear previous errors
    
    // Validation: Check that at least one tab is selected
    const selectedTabs = [];
    if (stepData.tabConfiguration?.overallView) {
      selectedTabs.push(...stepData.tabConfiguration.overallView.filter(tab => tab.selected));
    }
    if (stepData.tabConfiguration?.systemView) {
      selectedTabs.push(...stepData.tabConfiguration.systemView.filter(tab => tab.selected));
    }
    if (stepData.tabConfiguration?.customTabs) {
      selectedTabs.push(...stepData.tabConfiguration.customTabs.filter(tab => tab.selected));
    }
    
    if (selectedTabs.length === 0) {
      setApiError("At least one tab must be selected");
      setIsSaving(false);
      return;
    }
    
    // Validation: For each selected tab, at least one column must be selected
    const validationErrors = [];
    selectedTabs.forEach(tab => {
      const tabName = tab.name;
      const tabConfig = stepData.columnConfigurations?.[tabName];
      
      if (tabConfig && tabConfig.columns) {
        const hasSelectedColumn = tabConfig.columns.some(col => col.overallView === true || col.systemView === true);
        if (!hasSelectedColumn) {
          validationErrors.push(`At least one column must be selected for tab "${tabName}"`);
        }
      } else {
        validationErrors.push(`Column configuration is missing for tab "${tabName}"`);
      }
    });
    
    if (validationErrors.length > 0) {
      setApiError(validationErrors.join(". "));
      setIsSaving(false);
      return;
    }
    
    try {
      // Get required IDs
      const collabrixProjectID = getCollabrixProjectID();
      const cdmoOrgID = getCdmoOrgID();
      const sponsorMosymphonyID = getSponsorMosymphonyID();
      
      if (!collabrixProjectID || !cdmoOrgID) {
        const errorMsg = "Missing required information. Please complete the previous steps first.";
        setApiError(errorMsg);
        setIsSaving(false);
        return;
      }

      // Map stageMapping to campaignStageDetails
      const campaignStageDetails = (stepData.stageMapping || [])
        .filter((stage) => stage.selected)
        .map((stage) => ({
          collabrixCampaignID: stepData.collabrixCampaignID || null,
          stageNumber: stage.stageNumber || null,
          sponsorStageID: stage.sponsorStageID || "",
          cmoStageID: stage.cmoStageID || "",
          isActive: stage.selected || false,
          collabrixCampaignStageID: null, // Backend will handle this
        }));

      // Map materials to campaignBOMDetails
      const allMaterials = [
        ...(stepData.materialsNotProvidedByBMS || []).map((material) => ({
          collabrixCampaignID: stepData.collabrixCampaignID || null,
          collabrixBomID: material.collabrixBomID || null,
          collabrixProjectID: collabrixProjectID,
          cmoMaterialCode: material.cmoMaterialCode || "",
          cmoMaterialDescription: material.cmoMaterialDescription || "",
          cmoStageIDs: Array.isArray(material.usedInStage) ? material.usedInStage.join(",") : (material.usedInStage || ""),
          cmoUOM: material.cmoUOM || "",
          supplierName: material.supplier || "",
          countryOfOrigin: material.countryOfOrigin || "",
          isActive: true,
          sponsorMaterialCode: material.bo_material_code || "",
          sponsorMaterialDescription: material.material_description || "",
          sponsorUOM: material.bo_material_UOM || "",
          materialProvidedBySponsor: false,
        })),
        ...(stepData.materialsProvidedByBMS || []).map((material) => ({
          collabrixCampaignID: stepData.collabrixCampaignID || null,
          collabrixBomID: material.collabrixBomID || null,
          collabrixProjectID: collabrixProjectID,
          cmoMaterialCode: material.cmoMaterialCode || "",
          cmoMaterialDescription: material.cmoMaterialDescription || "",
          cmoStageIDs: Array.isArray(material.usedInStage) ? material.usedInStage.join(",") : (material.usedInStage || ""),
          cmoUOM: material.cmoUOM || "",
          supplierName: material.supplierName || "",
          countryOfOrigin: material.countryOfOrigin || "",
          isActive: true,
          sponsorMaterialCode: material.bmsMaterialCode || "",
          sponsorMaterialDescription: material.bmsMaterialDescription || "",
          sponsorUOM: material.bmsUOM || "",
          materialProvidedBySponsor: true,
        })),
      ];

      // Map tabConfiguration to campaignTabConfiguration
      const campaignTabConfiguration = [];
      
      if (stepData.tabConfiguration) {
        // Overall View tabs
        if (stepData.tabConfiguration.overallView) {
          stepData.tabConfiguration.overallView.forEach((tab) => {
            campaignTabConfiguration.push({
              collabrixCampaignID: stepData.collabrixCampaignID || null,
              viewTab: "overallView",
              tabType: "default",
              tabName: tab.name || "",
              newTabName: tab.renamedTab && tab.renamedTab !== "NA" ? tab.renamedTab : null,
              isActive: tab.selected || false,
              markForDelete: false,
              collabrixTabConfigID: null, // Backend will handle this
            });
          });
        }
        // System View tabs
        if (stepData.tabConfiguration.systemView) {
          stepData.tabConfiguration.systemView.forEach((tab) => {
            campaignTabConfiguration.push({
              collabrixCampaignID: stepData.collabrixCampaignID || null,
              viewTab: "systemView",
              tabType: "default",
              tabName: tab.name || "",
              newTabName: tab.renamedTab && tab.renamedTab !== "NA" ? tab.renamedTab : null,
              isActive: tab.selected || false,
              markForDelete: false,
              collabrixTabConfigID: null, // Backend will handle this
            });
          });
        }
        // Custom tabs
        if (stepData.tabConfiguration.customTabs) {
          stepData.tabConfiguration.customTabs.forEach((tab) => {
            campaignTabConfiguration.push({
              collabrixCampaignID: stepData.collabrixCampaignID || null,
              viewTab: tab.viewTab || "overallView",
              tabType: "custom",
              tabName: tab.name || "",
              newTabName: tab.renamedTab && tab.renamedTab !== "NA" ? tab.renamedTab : null,
              isActive: tab.selected || false,
              markForDelete: tab.markForDelete || false,
              collabrixTabConfigID: null, // Backend will handle this
            });
          });
        }
      }

      // Map columnConfigurations to campaignColumnConfiguration
      const campaignColumnConfiguration = [];
      
      if (stepData.columnConfigurations) {
        Object.keys(stepData.columnConfigurations).forEach((tabName) => {
          const tabConfig = stepData.columnConfigurations[tabName];
          
          if (tabConfig && tabConfig.columns) {
            tabConfig.columns.forEach((column) => {
              campaignColumnConfiguration.push({
                collabrixCampaignID: stepData.collabrixCampaignID || null,
                columnName: column.name || "",
                newColumnName: column.newColumnName && column.newColumnName !== "NA" ? column.newColumnName : null,
                isOverallViewSelected: column.overallView || false,
                isSystemViewSelected: column.systemView || false,
                columnType: "default",
                markForDelete: false,
                collabrixTabConfigID: null, // Backend will handle this
                collabrixColumnConfigID: null, // Backend will handle this
              });
            });
          }
        });
      }

      // Build payload for updateCampaignDetails API
      const payload = {
        campaignDetails: {
          collabrixProjectID: collabrixProjectID,
          collabrixCampaignID: stepData.collabrixCampaignID || null,
          campaignID: stepData.campaignID || "",
          poNumber: stepData.poNumber || "",
          productQuantityRequired: stepData.productQuantity || "",
          uom: stepData.uom || "KG",
          viewType: stepData.defaultView || "System",
          campaignStartDate: stepData.campaignStartDate 
            ? (stepData.campaignStartDate.format ? stepData.campaignStartDate.format("YYYY-MM-DD") : stepData.campaignStartDate)
            : null,
          campaignEndDate: stepData.campaignEndDate 
            ? (stepData.campaignEndDate.format ? stepData.campaignEndDate.format("YYYY-MM-DD") : stepData.campaignEndDate)
            : null,
          manufacturingFacility: stepData.manufacturingFacility || "",
          dataGranularityID: getDataGranularityID(stepData.dataGranularity),
          cdmoOrgID: cdmoOrgID,
          numberOfStages: stepData.numberOfStages ? parseInt(stepData.numberOfStages) : null,
          campaignStatusID: stepData.campaignStatusID || null,
          campaignStatusText: stepData.campaignStatusText || null,
          sponsorMosymphonyID: sponsorMosymphonyID,
          vendorMosymphonyID: cdmoOrgID, // Assuming cdmoOrgID is the vendor ID
        },
        campaignStageDetails: campaignStageDetails,
        campaignBOMDetails: allMaterials,
        campaignTabConfiguration: campaignTabConfiguration,
        campaignColumnConfiguration: campaignColumnConfiguration,
      };

      console.log("=== updateCampaignDetails API Payload ===");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=== END PAYLOAD ===");

      // Call API
      const response = await updateCampaignDetailsAPI(payload);
      
      if (response && response.status) {
        console.log("✅ updateCampaignDetails API Response:", response);
        
        // Store collabrixCampaignID if returned (for new campaigns)
        if (response.data?.collabrixCampaignID && !stepData.collabrixCampaignID) {
          const updatedStepData = {
            ...stepData,
            collabrixCampaignID: response.data.collabrixCampaignID,
          };
          setStepData(updatedStepData);
          updateStepData("campaignID", updatedStepData);
        } else {
          // Ensure latest data is saved to session storage
          updateStepData("campaignID", stepData);
        }

        // Only navigate on success
        if (markApiCallSuccessful) {
          markApiCallSuccessful(true, null, "Add Campaign ID");
        } else {
          handleNext();
        }
      } else {
        throw new Error(response?.message || "Failed to update campaign details");
      }
    } catch (error) {
      console.error("Error saving campaign ID:", error);
      // Show error message and do NOT navigate
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save campaign details. Please try again.";
      setApiError(errorMessage);
      setIsSaving(false);
    }
  }, [stepData, updateStepData, markApiCallSuccessful, handleNext, getCollabrixProjectID, getCdmoOrgID, getSponsorMosymphonyID, getDataGranularityID]);

  // Determine the effective read-only state
  const effectiveReadOnly = readOnly;

  const displayData = stepData;

  return (
    <Box style={{ padding: "20px 0" }}>
      <Typography
        variant="h6"
        className={parentClasses?.formTitle}
        style={{
          marginBottom: "24px",
        }}
      >
        Campaign Management
      </Typography>


      {/* Form Section */}
      <Paper
        elevation={0}
        style={{
          padding: "32px",
          backgroundColor: "transparent",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2}>
          {/* All fields in 2-per-row layout */}
          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Enter Campaign ID <span className={classes.requiredStar}>*</span>
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {displayData.campaignID || "N/A"}
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  value={displayData.campaignID}
                  onChange={(e) => handleFieldChange("campaignID", e.target.value)}
                  error={!!errors.campaignID}
                  helperText={errors.campaignID}
                  variant="outlined"
                  size="small"
                  placeholder="Enter campaign ID"
                  style={{ backgroundColor: "#fff" }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                PO Number <span className={classes.requiredStar}>*</span>
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {displayData.poNumber || "N/A"}
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  value={displayData.poNumber}
                  onChange={(e) => handleFieldChange("poNumber", e.target.value)}
                  error={!!errors.poNumber}
                  helperText={errors.poNumber}
                  variant="outlined"
                  size="small"
                  placeholder="Enter PO number"
                  style={{ backgroundColor: "#fff" }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Product Quantity Requirement
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {displayData.productQuantity || "N/A"}
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  value={displayData.productQuantity}
                  onChange={(e) => handleFieldChange("productQuantity", e.target.value)}
                  variant="outlined"
                  type="number"
                  size="small"
                  placeholder="Enter quantity"
                  style={{ backgroundColor: "#fff" }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Default View
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.defaultView || "N/A"}
                </Typography>
              ) : (
                <FormControl fullWidth variant="outlined" size="small" style={{ backgroundColor: "#fff" }}>
                  <Select
                    value={stepData.defaultView}
                    onChange={(e) => handleFieldChange("defaultView", e.target.value)}
                    displayEmpty
                  >
                    {DEFAULT_VIEW_OPTIONS.map((view) => (
                      <MenuItem key={view} value={view}>
                        {view}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Campaign Start Date
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.campaignStartDate ? (stepData.campaignStartDate.format ? stepData.campaignStartDate.format(localStorage.selectedDateFormat || "DD-MMM-YYYY") : stepData.campaignStartDate) : "N/A"}
                </Typography>
              ) : (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <DatePicker
                    fullWidth
                    variant="inline"
                    inputVariant="outlined"
                    format={localStorage.selectedDateFormat ? localStorage.selectedDateFormat : "DD-MMM-YYYY"}
                    name="campaignStartDate"
                    value={stepData.campaignStartDate || null}
                    onChange={(date) => handleFieldChange("campaignStartDate", date)}
                    placeholder="Select date"
                    size="small"
                    autoOk={true}
                    views={['date', 'month', 'year']}
                    openTo="date"
                    // Campaign start must be within project start/end
                    {...(projectStartDate && { minDate: projectStartDate })}
                    {...(projectEndDate && { maxDate: projectEndDate })}
                    style={{ marginTop: "4px" }}
                    InputProps={{
                      endAdornment: (
                        <EventTwoToneIcon
                          style={{ color: '#0000008a', cursor: 'pointer' }}
                        />
                      ),
                    }}
                  />
                </MuiPickersUtilsProvider>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Campaign End Date
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.campaignEndDate ? (stepData.campaignEndDate.format ? stepData.campaignEndDate.format(localStorage.selectedDateFormat || "DD-MMM-YYYY") : stepData.campaignEndDate) : "N/A"}
                </Typography>
              ) : (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <DatePicker
                    fullWidth
                    variant="inline"
                    inputVariant="outlined"
                    format={localStorage.selectedDateFormat ? localStorage.selectedDateFormat : "DD-MMM-YYYY"}
                    name="campaignEndDate"
                    value={stepData.campaignEndDate || null}
                    onChange={(date) => handleFieldChange("campaignEndDate", date)}
                    placeholder="Select date"
                    size="small"
                    autoOk={true}
                    views={['date', 'month', 'year']}
                    openTo="date"
                    // End date cannot be before campaign start, and must be within project range
                    {...(stepData.campaignStartDate || projectStartDate
                      ? { minDate: stepData.campaignStartDate || projectStartDate }
                      : {})}
                    {...(projectEndDate && { maxDate: projectEndDate })}
                    disabled={!stepData.campaignStartDate}
                    style={{ marginTop: "4px" }}
                    InputProps={{
                      endAdornment: (
                        <EventTwoToneIcon
                          style={{ color: '#0000008a', cursor: 'pointer' }}
                        />
                      ),
                    }}
                  />
                </MuiPickersUtilsProvider>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Data Granularity
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.dataGranularityName || stepData.dataGranularity || "N/A"}
                </Typography>
              ) : (
                <FormControl fullWidth variant="outlined" size="small" style={{ backgroundColor: "#fff" }}>
                  <Select
                    value={stepData.dataGranularity}
                    onChange={(e) => handleFieldChange("dataGranularity", e.target.value)}
                    displayEmpty
                  >
                    {dataGranularityOptions.map((option) => (
                      <MenuItem key={option.dataGranularityLevelID} value={option.dataGranularityLevelID}>
                        {option.dataGranularityLevelName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                Manufacturing Facility / Site
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.manufacturingFacility || "N/A"}
                </Typography>
              ) : (
                <TextField
                  fullWidth
                  value={stepData.manufacturingFacility}
                  onChange={(e) => handleFieldChange("manufacturingFacility", e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Enter facility name"
                  style={{ backgroundColor: "#fff" }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box style={{ marginBottom: "24px" }}>
              <Typography
                component="label"
                style={{
                  marginBottom: "4px",
                  fontWeight: 700,
                  fontSize: "16px",
                  wordWrap: "break-word",
                  display: "block",
                }}
              >
                UOM (Unit of Measure)
              </Typography>
              {effectiveReadOnly ? (
                <Typography className={classes.readOnlyValue}>
                  {stepData.uom || "N/A"}
                </Typography>
              ) : (
                <FormControl fullWidth variant="outlined" size="small" style={{ backgroundColor: "#fff" }}>
                  <Select
                    value={stepData.uom}
                    onChange={(e) => handleFieldChange("uom", e.target.value)}
                    displayEmpty
                  >
                    {UOM_OPTIONS.map((uom) => (
                      <MenuItem key={uom} value={uom}>
                        {uom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stage Mapping Section */}
      <Box style={{ marginTop: "40px" }}>
        <Typography
          variant="h6"
          className={parentClasses?.formTitle}
          style={{ marginBottom: "24px" }}
        >
          Stage Mapping
        </Typography>

        {/* Number Of Stages Input */}
        <Box style={{ marginBottom: "24px" }}>
          <Typography
            component="label"
            style={{
              marginBottom: "4px",
              fontWeight: 700,
              fontSize: "16px",
              wordWrap: "break-word",
              display: "block",
            }}
          >
            Number Of Stages
          </Typography>
          {readOnly ? (
            <Typography className={classes.readOnlyValue} style={{ maxWidth: "100px" }}>
              {stepData.numberOfStages || "N/A"}
            </Typography>
          ) : (
            <TextField
              value={stepData.numberOfStages}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty string or numbers between 1-10
                if (value === "" || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 10)) {
                  handleFieldChange("numberOfStages", value);
                }
              }}
              variant="outlined"
              type="number"
              size="small"
              placeholder="Max 10"
              inputProps={{ min: 1, max: 10 }}
              style={{ backgroundColor: "#fff", width: "100px" }}
              helperText={stepData.numberOfStages > 10 ? "Maximum 10 stages allowed" : ""}
              error={stepData.numberOfStages > 10}
            />
          )}
        </Box>

        <TableContainer component={Paper} style={{ border: "1px solid #e0e0e0", marginBottom: "24px" }}>
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              {effectiveReadOnly ? (
                <>
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "35%" }} />
                  <col style={{ width: "35%" }} />
                  <col style={{ width: "15%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "35%" }} />
                  <col style={{ width: "35%" }} />
                  <col style={{ width: "10%" }} />
                </>
              )}
            </colgroup>
            <TableHead style={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Stage Number</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Sponsor Stage ID</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO Stage ID</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "0.875rem", fontWeight: 600 }} align="center">Selection</TableCell>
              </TableRow>
            </TableHead>
                <TableBody>
                  {stepData.stageMapping.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ padding: "24px", color: "#757575" }}>
                        {effectiveReadOnly ? "No stages added." : "Enter the number of stages above to create stage rows."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    stepData.stageMapping.map((stage, index) => (
                      <TableRow key={stage.id || index}>
                        <TableCell style={{ padding: "8px" }}>{stage.stageNumber}</TableCell>
                        <TableCell style={{ padding: "8px" }}>
                          {effectiveReadOnly ? (
                            <Typography className={classes.readOnlyTableValue}>
                              {stage.sponsorStageID || "N/A"}
                            </Typography>
                          ) : (
                            <TextField
                              fullWidth
                              value={stage.sponsorStageID}
                              onChange={(e) => handleStageChange(index, "sponsorStageID", e.target.value)}
                              variant="outlined"
                              size="small"
                              placeholder="Enter Sponsor Stage ID"
                            />
                          )}
                        </TableCell>
                        <TableCell style={{ padding: "8px" }}>
                          {effectiveReadOnly ? (
                            <Typography className={classes.readOnlyTableValue}>
                              {stage.cmoStageID || "N/A"}
                            </Typography>
                          ) : (
                            <TextField
                              fullWidth
                              value={stage.cmoStageID}
                              onChange={(e) => handleStageChange(index, "cmoStageID", e.target.value)}
                              variant="outlined"
                              size="small"
                              placeholder="Enter CMO Stage ID"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ padding: "8px" }}>
                          <span style={{ position: "relative" }}>
                            <IOSSwitch
                              className="ios-switch"
                              disabled={readOnly}
                              checked={stage.selected}
                              onChange={(e) => handleStageChange(index, "selected", e.target.checked)}
                            />
                            {stage.selected && <Check className="checked-switch" />}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
      </Box>

      {/* List of Materials not provided by Sponsor */}
      <Box style={{ marginTop: "40px" }}>
        <Typography
          variant="h6"
          className={classes?.formTitle}
          style={{ marginBottom: "24px" }}
        >
          List of Materials not provided by Sponsor
        </Typography>

        <TableContainer component={Paper} style={{ border: "1px solid #e0e0e0", marginBottom: "24px" }}>
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              {effectiveReadOnly ? (
                <>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "10%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                </>
              )}
            </colgroup>
            <TableHead style={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO Material Code</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO Material Description</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Used In Stage</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO UOM</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Supplier</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Country Of Origin</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "0.875rem", fontWeight: 600 }} align="center">Selection</TableCell>
                {!effectiveReadOnly && (
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem", fontWeight: 600 }} align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {stepData.materialsNotProvidedByBMS.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={effectiveReadOnly ? 7 : 8} align="center" style={{ padding: "24px", color: "#757575" }}>
                    {effectiveReadOnly ? "No materials added." : "No materials added. Click \"+ Add New Material\" to add a material."}
                  </TableCell>
                </TableRow>
              ) : (
                stepData.materialsNotProvidedByBMS.map((material, index) => (
                  <TableRow key={material.id}>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoMaterialCode || "N/A"}</Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.cmoMaterialCode}
                          onChange={(e) => handleMaterialNotProvidedChange(index, "cmoMaterialCode", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter code"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoMaterialDescription || "N/A"}</Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.cmoMaterialDescription}
                          onChange={(e) => handleMaterialNotProvidedChange(index, "cmoMaterialDescription", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter description"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {(material.usedInStage || []).length > 0 ? (material.usedInStage || []).join(", ") : "N/A"}
                        </Typography>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            multiple
                            value={material.usedInStage || []}
                            onChange={(e) => handleMaterialNotProvidedChange(index, "usedInStage", e.target.value)}
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) return "Select stages";
                              return selected.join(", ");
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                            }}
                            displayEmpty
                          >
                            {getAvailableStages().length === 0 ? (
                              <MenuItem disabled value="">
                                No stages available. Add stages in Stage Mapping above.
                              </MenuItem>
                            ) : (
                              getAvailableStages().map((stage) => (
                                <MenuItem key={stage} value={stage}>
                                  <Checkbox checked={(material.usedInStage || []).indexOf(stage) > -1} />
                                  <ListItemText primary={stage} />
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoUOM || "N/A"}</Typography>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            value={material.cmoUOM}
                            onChange={(e) => handleMaterialNotProvidedChange(index, "cmoUOM", e.target.value)}
                          >
                            {UOM_OPTIONS.map((uom) => (
                              <MenuItem key={uom} value={uom}>
                                {uom}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {material.supplier || "N/A"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.supplier}
                          onChange={(e) => handleMaterialNotProvidedChange(index, "supplier", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter supplier"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {material.countryOfOrigin || "N/A"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.countryOfOrigin}
                          onChange={(e) => handleMaterialNotProvidedChange(index, "countryOfOrigin", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter country"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "8px" }}>
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          disabled={readOnly}
                          checked={material.selected}
                          onChange={(e) => handleMaterialNotProvidedChange(index, "selected", e.target.checked)}
                        />
                        {material.selected && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                    {!effectiveReadOnly && (
                      <TableCell align="center" style={{ padding: "8px" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMaterialNotProvided(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!readOnly && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddMaterialNotProvided}
            style={{
              marginTop: "8px",
              textTransform: "none",
              borderColor: "#000000",
              color: "#000000",
              borderRadius: "20px",
              fontWeight: 600,
            }}
          >
            Add New Material
          </Button>
        )}
      </Box>

      {/* List of Materials provided by Sponsor */}
      <Box style={{ marginTop: "40px" }}>
        <Typography
          variant="h6"
          className={classes?.formTitle}
          style={{ marginBottom: "24px" }}
        >
          List of Materials provided by Sponsor
        </Typography>

        <TableContainer component={Paper} style={{ border: "1px solid #e0e0e0", marginBottom: "24px" }}>
          <Table style={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              {effectiveReadOnly ? (
                <>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "8%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "5%" }} />
                </>
              )}
            </colgroup>
            <TableHead style={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Sponsor Material Code</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Sponsor Material Description</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO Material Code</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO Material Description</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Used In Stage</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>Sponsor UOM</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "16px", fontWeight: 600 }}>CMO UOM</TableCell>
                <TableCell style={{ padding: "8px", fontSize: "0.875rem", fontWeight: 600 }} align="center">Selection</TableCell>
                {!effectiveReadOnly && (
                  <TableCell style={{ padding: "8px", fontSize: "0.875rem", fontWeight: 600 }} align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {stepData.materialsProvidedByBMS.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={effectiveReadOnly ? 8 : 9} align="center" style={{ padding: "24px", color: "#757575" }}>
                    {effectiveReadOnly ? "No materials added." : "No materials added. Click \"+ Add New Material\" to add a material."}
                  </TableCell>
                </TableRow>
              ) : (
                stepData.materialsProvidedByBMS.map((material, index) => (
                  <TableRow key={material.id}>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {material.bmsMaterialCode || "N/A"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.bmsMaterialCode}
                          onChange={(e) => handleMaterialProvidedChange(index, "bmsMaterialCode", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter code"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {material.bmsMaterialDescription || "N/A"}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.bmsMaterialDescription}
                          onChange={(e) => handleMaterialProvidedChange(index, "bmsMaterialDescription", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter description"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoMaterialCode || "N/A"}</Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.cmoMaterialCode}
                          onChange={(e) => handleMaterialProvidedChange(index, "cmoMaterialCode", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter code"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoMaterialDescription || "N/A"}</Typography>
                      ) : (
                        <TextField
                          fullWidth
                          value={material.cmoMaterialDescription}
                          onChange={(e) => handleMaterialProvidedChange(index, "cmoMaterialDescription", e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Enter description"
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {(material.usedInStage || []).length > 0 ? (Array.isArray(material.usedInStage) ? material.usedInStage.join(", ") : material.usedInStage) : "N/A"}
                        </Typography>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            multiple
                            value={Array.isArray(material.usedInStage) ? material.usedInStage : (material.usedInStage ? [material.usedInStage] : [])}
                            onChange={(e) => handleMaterialProvidedChange(index, "usedInStage", e.target.value)}
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) return "Select stages";
                              return selected.join(", ");
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                            }}
                            displayEmpty
                          >
                            {getAvailableStages().length === 0 ? (
                              <MenuItem disabled value="">
                                No stages available. Add stages in Stage Mapping above.
                              </MenuItem>
                            ) : (
                              getAvailableStages().map((stage) => (
                                <MenuItem key={stage} value={stage}>
                                  <Checkbox checked={(Array.isArray(material.usedInStage) ? material.usedInStage : (material.usedInStage ? [material.usedInStage] : [])).indexOf(stage) > -1} />
                                  <ListItemText primary={stage} />
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>
                          {material.bmsUOM || "N/A"}
                        </Typography>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            value={material.bmsUOM}
                            onChange={(e) => handleMaterialProvidedChange(index, "bmsUOM", e.target.value)}
                          >
                            {UOM_OPTIONS.map((uom) => (
                              <MenuItem key={uom} value={uom}>
                                {uom}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {effectiveReadOnly ? (
                        <Typography className={classes.readOnlyTableValue}>{material.cmoUOM || "N/A"}</Typography>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            value={material.cmoUOM}
                            onChange={(e) => handleMaterialProvidedChange(index, "cmoUOM", e.target.value)}
                          >
                            {UOM_OPTIONS.map((uom) => (
                              <MenuItem key={uom} value={uom}>
                                {uom}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell align="center" style={{ padding: "8px" }}>
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          disabled={readOnly}
                          checked={material.selected}
                          onChange={(e) => handleMaterialProvidedChange(index, "selected", e.target.checked)}
                        />
                        {material.selected && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                    {!effectiveReadOnly && (
                      <TableCell align="center" style={{ padding: "8px" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMaterialProvided(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!readOnly && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddMaterialProvided}
            style={{
              marginTop: "8px",
              textTransform: "none",
              borderColor: "#000000",
              color: "#000000",
              borderRadius: "20px",
              fontWeight: 600,
            }}
          >
            Add New Material
          </Button>
        )}
      </Box>

      {/* Tab View Configuration Section */}
      <Box style={{ marginTop: "40px" }}>
        <Typography
          variant="h6"
          className={classes?.formTitle}
          style={{ marginBottom: "24px" }}
        >
          Tab Configuration
        </Typography>

        <Grid container spacing={3}>
          {/* Overall View Selection */}
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} style={{ border: "1px solid #e0e0e0", maxWidth: "100%" }}>
              <Table size="small" style={{ width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                  {effectiveReadOnly ? (
                    <>
                      <col style={{ width: "25%" }} />
                      <col style={{ width: "35%" }} />
                      <col style={{ width: "40%" }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "35%" }} />
                      <col style={{ width: "15%" }} />
                    </>
                  )}
                </colgroup>
                <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Overall View Selection</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Tab Name</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Renamed Tab</TableCell>
                    {!effectiveReadOnly && (
                      <TableCell style={{ fontWeight: 600 }}>Action</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stepData.tabConfiguration.overallView.map((tab) => (
                    <TableRow key={tab.id}>
                      <TableCell>
                        <span style={{ position: "relative" }}>
                          <IOSSwitch
                            className="ios-switch"
                            disabled={effectiveReadOnly}
                            checked={tab.selected}
                            onChange={(e) => {
                              const updated = { ...stepData };
                              const tabIndex = updated.tabConfiguration.overallView.findIndex((t) => t.id === tab.id);
                              if (tabIndex !== -1) {
                                updated.tabConfiguration.overallView[tabIndex].selected = e.target.checked;
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                              }
                            }}
                          />
                          {tab.selected && <Check className="checked-switch" />}
                        </span>
                      </TableCell>
                      <TableCell>
                        {effectiveReadOnly ? (
                          <Box className={classes.readOnlyTableValue}>
                            {tab.name}
                          </Box>
                        ) : (
                          tab.name
                        )}
                      </TableCell>
                      <TableCell>
                        {effectiveReadOnly ? (
                          <Box className={classes.readOnlyTableValue}>
                            {tab.renamedTab || "NA"}
                          </Box>
                        ) : editingTabId === tab.id && editingTabType === "overallView" ? (
                          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <TextField
                              size="small"
                              value={editingTabValue}
                              onChange={(e) => setEditingTabValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const updated = { ...stepData };
                                  const tabIndex = updated.tabConfiguration.overallView.findIndex((t) => t.id === tab.id);
                                  if (tabIndex !== -1) {
                                    updated.tabConfiguration.overallView[tabIndex].renamedTab = editingTabValue.trim() || "NA";
                                    setStepData(updated);
                                    updateStepData("campaignID", updated);
                                  }
                                  setEditingTabId(null);
                                  setEditingTabValue("");
                                  setEditingTabType(null);
                                } else if (e.key === "Escape") {
                                  setEditingTabId(null);
                                  setEditingTabValue("");
                                  setEditingTabType(null);
                                }
                              }}
                              autoFocus
                              style={{ flex: 1 }}
                              variant="outlined"
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updated = { ...stepData };
                                const tabIndex = updated.tabConfiguration.overallView.findIndex((t) => t.id === tab.id);
                                if (tabIndex !== -1) {
                                  updated.tabConfiguration.overallView[tabIndex].renamedTab = editingTabValue.trim() || "NA";
                                  setStepData(updated);
                                  updateStepData("campaignID", updated);
                                }
                                setEditingTabId(null);
                                setEditingTabValue("");
                                setEditingTabType(null);
                              }}
                            >
                              <Check style={{ fontSize: 16, color: "#4caf50" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingTabId(null);
                                setEditingTabValue("");
                                setEditingTabType(null);
                              }}
                            >
                              <DeleteIcon style={{ fontSize: 16, color: "#f44336" }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Typography variant="body2">
                              {tab.renamedTab || "NA"}
                            </Typography>
                            <IconButton
                              size="small"
                              style={{ padding: "4px", marginLeft: "4px" }}
                              onClick={() => {
                                setEditingTabId(tab.id);
                                setEditingTabValue(tab.renamedTab || "NA");
                                setEditingTabType("overallView");
                              }}
                            >
                              <EditIcon style={{ fontSize: 22, color: "#ec6535" }} />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      {!effectiveReadOnly && (
                        <TableCell align="center">
                          {isNewlyAddedTab(tab.name) && (
                            <IconButton
                              size="small"
                              style={{ padding: "4px" }}
                              onClick={() => {
                                const updated = { ...stepData };
                                // Remove from both views
                                updated.tabConfiguration.overallView = updated.tabConfiguration.overallView.filter(
                                  (t) => t.name !== tab.name
                                );
                                updated.tabConfiguration.systemView = updated.tabConfiguration.systemView.filter(
                                  (t) => t.name !== tab.name
                                );
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                              }}
                            >
                              <DeleteIcon style={{ fontSize: 22, color: "#f44336" }} />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* Add new tab input for Overall View - only show when not readOnly */}
                  {!effectiveReadOnly && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <TextField
                            placeholder="Type Tab Name Here"
                            size="small"
                            fullWidth
                            style={{ backgroundColor: "#fff" }}
                            value={newTabNameOverall || ""}
                            onChange={(e) => {
                              setNewTabNameOverall(e.target.value);
                            }}
                          />
                          {newTabNameOverall && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setNewTabNameOverall("");
                              }}
                            >
                              <CloseIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            disabled={!newTabNameOverall || !newTabNameOverall.trim()}
                            onClick={() => {
                              if (newTabNameOverall.trim()) {
                                const updated = { ...stepData };
                                const maxId = Math.max(
                                  ...updated.tabConfiguration.overallView.map((t) => t.id),
                                  ...updated.tabConfiguration.systemView.map((t) => t.id),
                                  0
                                );
                                const newTab = {
                                  id: maxId + 1,
                                  name: newTabNameOverall.trim(),
                                  selected: true,
                                  renamedTab: "NA",
                                };
                                // Add to both overall and system views with same configuration
                                updated.tabConfiguration.overallView.push(newTab);
                                updated.tabConfiguration.systemView.push({ ...newTab });
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                                setNewTabNameOverall(""); // Clear input and show empty row again
                              }
                            }}
                            style={{
                              opacity: (!newTabNameOverall || !newTabNameOverall.trim()) ? 0.5 : 1,
                            }}
                          >
                            <SaveIcon style={{ fontSize: 18, color: "#ec6535" }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* System View Selection */}
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} style={{ border: "1px solid #e0e0e0", maxWidth: "100%" }}>
              <Table size="small" style={{ width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                  {effectiveReadOnly ? (
                    <>
                      <col style={{ width: "25%" }} />
                      <col style={{ width: "35%" }} />
                      <col style={{ width: "40%" }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "35%" }} />
                      <col style={{ width: "15%" }} />
                    </>
                  )}
                </colgroup>
                <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>System View Selection</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Tab Name</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Renamed Tab</TableCell>
                    {!effectiveReadOnly && (
                      <TableCell style={{ fontWeight: 600 }}>Action</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stepData.tabConfiguration.systemView.map((tab) => (
                    <TableRow key={tab.id}>
                      <TableCell>
                        <span style={{ position: "relative" }}>
                          <IOSSwitch
                            className="ios-switch"
                            disabled={effectiveReadOnly}
                            checked={tab.selected}
                            onChange={(e) => {
                              const updated = { ...stepData };
                              const tabIndex = updated.tabConfiguration.systemView.findIndex((t) => t.id === tab.id);
                              if (tabIndex !== -1) {
                                updated.tabConfiguration.systemView[tabIndex].selected = e.target.checked;
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                              }
                            }}
                          />
                          {tab.selected && <Check className="checked-switch" />}
                        </span>
                      </TableCell>
                      <TableCell>
                        {effectiveReadOnly ? (
                          <Typography className={classes.readOnlyTableValue}>
                            {tab.name}
                          </Typography>
                        ) : (
                          tab.name
                        )}
                      </TableCell>
                      <TableCell>
                        {effectiveReadOnly ? (
                          <Typography className={classes.readOnlyTableValue}>
                            {tab.renamedTab === "NA" ? tab.name : tab.renamedTab}
                          </Typography>
                        ) : editingTabId === tab.id && editingTabType === "systemView" ? (
                          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <TextField
                              size="small"
                              value={editingTabValue}
                              onChange={(e) => setEditingTabValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const updated = { ...stepData };
                                  const tabIndex = updated.tabConfiguration.systemView.findIndex((t) => t.id === tab.id);
                                  if (tabIndex !== -1) {
                                    updated.tabConfiguration.systemView[tabIndex].renamedTab = editingTabValue.trim() || "NA";
                                    setStepData(updated);
                                    updateStepData("campaignID", updated);
                                  }
                                  setEditingTabId(null);
                                  setEditingTabValue("");
                                  setEditingTabType(null);
                                } else if (e.key === "Escape") {
                                  setEditingTabId(null);
                                  setEditingTabValue("");
                                  setEditingTabType(null);
                                }
                              }}
                              autoFocus
                              style={{ flex: 1 }}
                              variant="outlined"
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updated = { ...stepData };
                                const tabIndex = updated.tabConfiguration.systemView.findIndex((t) => t.id === tab.id);
                                if (tabIndex !== -1) {
                                  updated.tabConfiguration.systemView[tabIndex].renamedTab = editingTabValue.trim() || "NA";
                                  setStepData(updated);
                                  updateStepData("campaignID", updated);
                                }
                                setEditingTabId(null);
                                setEditingTabValue("");
                                setEditingTabType(null);
                              }}
                            >
                              <Check style={{ fontSize: 16, color: "#4caf50" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingTabId(null);
                                setEditingTabValue("");
                                setEditingTabType(null);
                              }}
                            >
                              <DeleteIcon style={{ fontSize: 16, color: "#f44336" }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <Typography variant="body2">
                              {tab.renamedTab || "NA"}
                            </Typography>
                            <IconButton
                              size="small"
                              style={{ padding: "4px", marginLeft: "4px" }}
                              onClick={() => {
                                setEditingTabId(tab.id);
                                setEditingTabValue(tab.renamedTab || "NA");
                                setEditingTabType("systemView");
                              }}
                            >
                              <EditIcon style={{ fontSize: 22, color: "#ec6535" }} />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      {!effectiveReadOnly && (
                        <TableCell align="center">
                          {isNewlyAddedTab(tab.name) && (
                            <IconButton
                              size="small"
                              style={{ padding: "4px" }}
                              onClick={() => {
                                const updated = { ...stepData };
                                // Remove from both views
                                updated.tabConfiguration.overallView = updated.tabConfiguration.overallView.filter(
                                  (t) => t.name !== tab.name
                                );
                                updated.tabConfiguration.systemView = updated.tabConfiguration.systemView.filter(
                                  (t) => t.name !== tab.name
                                );
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                              }}
                            >
                              <DeleteIcon style={{ fontSize: 22, color: "#f44336" }} />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* Add new tab input for System View - only show when not readOnly */}
                  {!effectiveReadOnly && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <TextField
                            placeholder="Type Tab Name Here"
                            size="small"
                            fullWidth
                            style={{ backgroundColor: "#fff" }}
                            value={newTabNameSystem || ""}
                            onChange={(e) => {
                              setNewTabNameSystem(e.target.value);
                            }}
                          />
                          {newTabNameSystem && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setNewTabNameSystem("");
                              }}
                            >
                              <CloseIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            disabled={!newTabNameSystem || !newTabNameSystem.trim()}
                            onClick={() => {
                              if (newTabNameSystem.trim()) {
                                const updated = { ...stepData };
                                const maxId = Math.max(
                                  ...updated.tabConfiguration.overallView.map((t) => t.id),
                                  ...updated.tabConfiguration.systemView.map((t) => t.id),
                                  0
                                );
                                const newTab = {
                                  id: maxId + 1,
                                  name: newTabNameSystem.trim(),
                                  selected: true,
                                  renamedTab: "NA",
                                };
                                // Add to both overall and system views with same configuration
                                updated.tabConfiguration.overallView.push(newTab);
                                updated.tabConfiguration.systemView.push({ ...newTab });
                                setStepData(updated);
                                updateStepData("campaignID", updated);
                                setNewTabNameSystem(""); // Clear input and show empty row again
                              }
                            }}
                            style={{
                              opacity: (!newTabNameSystem || !newTabNameSystem.trim()) ? 0.5 : 1,
                            }}
                          >
                            <SaveIcon style={{ fontSize: 18, color: "#ec6535" }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

      {/* Column Configuration Section */}
      <Box style={{ marginTop: "40px" }}>
        <Typography
          variant="h6"
          className={classes?.formTitle}
          style={{ marginBottom: "24px" }}
        >
          Column Configuration
        </Typography>

        {Object.keys(COLUMN_CONFIGS).map((tabName) => {
          const columns = stepData.columnConfigurations[tabName]?.columns || [];
          const newColumnName = stepData.columnConfigurations[tabName]?.newColumnName || "";

          return (
            <Box key={tabName} style={{ marginBottom: "40px" }}>
              <Typography
                variant="h6"
                className={parentClasses?.formTitle}
                style={{
                  marginBottom: "16px",
                  fontSize: "18px",
                }}
              >
                {tabName}
              </Typography>
              <TableContainer 
                component={Paper} 
                style={{ 
                  border: "1px solid #e0e0e0", 
                  width: "fit-content",
                  display: "inline-block"
                }}
              >
                <Table size="small" style={{ width: "auto", minWidth: "500px" }}>
                  <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600 }}>Column Name</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Overall View</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>System View</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Renamed Column</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((column) => (
                      <TableRow key={column.name}>
                        <TableCell>{column.name}</TableCell>
                        <TableCell>
                          <span style={{ position: "relative" }}>
                            <IOSSwitch
                              className="ios-switch"
                              checked={column.overallView}
                              onChange={() => handleColumnToggle(tabName, column.name, "overallView")}
                              color="primary"
                              disabled={effectiveReadOnly}
                            />
                            {column.overallView && <Check className="checked-switch" />}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span style={{ position: "relative" }}>
                            <IOSSwitch
                              className="ios-switch"
                              checked={column.systemView}
                              onChange={() => handleColumnToggle(tabName, column.name, "systemView")}
                              color="primary"
                              disabled={effectiveReadOnly}
                            />
                            {column.systemView && <Check className="checked-switch" />}
                          </span>
                        </TableCell>
                        <TableCell>
                          {effectiveReadOnly ? (
                            <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
                              {column.renamedTab || "NA"}
                            </Typography>
                          ) : editingColumn?.tabName === tabName && editingColumn?.columnName === column.name ? (
                            <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <TextField
                                size="small"
                                value={editingColumnValue}
                                onChange={(e) => setEditingColumnValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveColumnRename(tabName, column.name);
                                  } else if (e.key === "Escape") {
                                    handleCancelColumnRename();
                                  }
                                }}
                                autoFocus
                                style={{ flex: 1 }}
                                variant="outlined"
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleSaveColumnRename(tabName, column.name)}
                              >
                                <Check style={{ fontSize: 16, color: "#4caf50" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelColumnRename}
                              >
                                <DeleteIcon style={{ fontSize: 16, color: "#f44336" }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <Typography style={{ flex: 0, fontSize: "16px", fontWeight: 400 }}>
                                {column.renamedTab || "NA"}
                              </Typography>
                              <IconButton
                                size="small"
                                style={{ padding: "4px", marginLeft: "4px" }}
                                onClick={() => {
                                  setEditingColumn({ tabName, columnName: column.name });
                                  setEditingColumnValue(column.renamedTab || "NA");
                                }}
                              >
                                <EditIcon style={{ fontSize: 22, color: "#ec6535" }} />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {!effectiveReadOnly && isNewlyAddedColumn(tabName, column.name) && (
                            <IconButton
                              size="small"
                              style={{ padding: "4px" }}
                              onClick={() => handleDeleteColumn(tabName, column.name)}
                            >
                              <DeleteIcon style={{ fontSize: 22, color: "#f44336" }} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Add new column row */}
                    {!effectiveReadOnly && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <TextField
                              placeholder="Type Column Name Here"
                              size="small"
                              fullWidth
                              style={{ backgroundColor: "#fff" }}
                              value={newColumnName}
                              onChange={(e) => {
                                setStepData((prev) => {
                                  const updated = JSON.parse(JSON.stringify(prev));
                                  updated.columnConfigurations[tabName].newColumnName = e.target.value;
                                  return updated;
                                });
                              }}
                            />
                            {newColumnName && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setStepData((prev) => {
                                    const updated = JSON.parse(JSON.stringify(prev));
                                    updated.columnConfigurations[tabName].newColumnName = "";
                                    return updated;
                                  });
                                }}
                              >
                                <CloseIcon style={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleAddColumn(tabName)}
                              disabled={!newColumnName || !newColumnName.trim()}
                              style={{
                                opacity: (!newColumnName || !newColumnName.trim()) ? 0.5 : 1,
                              }}
                            >
                              <SaveIcon style={{ fontSize: 18, color: "#ec6535" }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </Box>

      {/* API Error Message */}
      {apiError && (
        <Box
          style={{
            marginTop: "24px",
            padding: "12px 16px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <Typography style={{ color: "#d32f2f", fontSize: "14px", fontWeight: 500 }}>
            {apiError}
          </Typography>
        </Box>
      )}

      {/* Navigation Buttons - Hidden when used in review page */}
      {!hideStepActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "80px",
            paddingTop: "24px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="contained"
            onClick={handleBack}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#9E9E9E",
              color: "#fff",
              padding: "10px 24px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: 30,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#ec6535",
              color: "#fff",
              padding: "10px 32px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 4px rgba(236, 101, 53, 0.3)",
              borderRadius: 30,
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
                Saving...
              </>
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddCampaignID;
